import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import {
  ANALYZE_DIAMOND,
  FORCE_RECALIBRATE,
  GET_DIAMOND_ANALYSES,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import ActionButton from "./ActionButton";
import NavButton from "./NavButton";

type AnalysisActionsProps = {
  diamondID: string;
  // The picture shown below, the one Recalibrate redoes.
  imageID?: string;
  // Whether the stone has an original or a pattern — the pictures a color is read from.
  canAnalyze: boolean;
};

// The analysis page's buttons, on one line under the pictures: Analyze reads the stone's color again
// from what its calibration stored; Recalibrate (admins) redoes the calibration of the picture shown
// below, and with it the analysis; Find similar opens the stones of the nearest color, once there is
// one.
const AnalysisActions = ({
  diamondID,
  imageID,
  canAnalyze,
}: AnalysisActionsProps) => {
  const token = useAppSelector((state) => state.auth.token);
  const role = useAppSelector((state) => state.auth.role);
  const canEdit = role === "Admin";
  const [analyzeDiamond] = useMutation(ANALYZE_DIAMOND, {
    ...tokenOptions(token),
    // The analysis section reads the same query, and the grading page the rankings, so either shows
    // the new reading once it lands.
    refetchQueries: ["DiamondAnalyses", "DiamondColorRanking"],
    awaitRefetchQueries: true,
  });
  const [forceRecalibrate] = useMutation(
    FORCE_RECALIBRATE,
    tokenOptions(token),
  );
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [recalibrating, setRecalibrating] = useState<boolean>(false);

  const { data } = useQuery(GET_DIAMOND_ANALYSES, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'diamondID == "' + diamondID + '"' },
  });
  const analysis = data?.diamondAnalyses?.items[0];

  const handleAnalyze = () => {
    setAnalyzing(true);
    analyzeDiamond({ variables: { diamondId: diamondID } }).finally(() =>
      setAnalyzing(false),
    );
  };

  // The mutation resolves only once the worker is done. The new results sit behind the same REST
  // image URLs the browser has cached, so a GraphQL refetch alone would keep showing the stale
  // pictures — reload the page so every image is fetched afresh.
  const handleRecalibrate = () => {
    setRecalibrating(true);
    forceRecalibrate({ variables: { imageId: imageID } })
      .then(() => window.location.reload())
      .catch(() => setRecalibrating(false));
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
      {canAnalyze && (
        <ActionButton
          onClick={handleAnalyze}
          disabled={analyzing || recalibrating}
        >
          Analyze
        </ActionButton>
      )}
      {canEdit && imageID && (
        <ActionButton
          onClick={handleRecalibrate}
          disabled={analyzing || recalibrating}
        >
          Recalibrate
        </ActionButton>
      )}
      {analysis?.oKH != null && (
        <NavButton
          onClick={() =>
            window.open("/analytics/similar/" + diamondID, "_blank")
          }
        >
          Find similar
        </NavButton>
      )}
      {(analyzing || recalibrating) && <Box>Please wait while loading...</Box>}
    </Stack>
  );
};

export default AnalysisActions;
